import ProductCard from "../components/ProductCard";
import {
  usePublicMarketplaceSearchCachedQuery,
  useUserMarketplaceSearchCachedQuery,
  useUserMarketplaceSuggestionsCachedQuery,
  usePublicMarketplaceSuggestionsCachedQuery,
  useProfileReadQuery,
} from "../store/api.ts";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import AdvancedMeasurementsProModal from "../components/AdvancedMeasurementsProModal.js";
import StableMasonryGrid from "../components/StableMasonryGrid.js";
import { useSearchParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useEffect, useMemo, useRef, useState } from "react";
import NewNavbar from "../components/NewNavbar.js";
import FloatingChatBar from "../components/FloatingChatBar.js";
import { useMarketplaceChat } from "../context/MarketplaceChatProvider.js";
import { getPendingSurveyToken, getVisibleCardMatchPercentage} from "../utils.js";
import AuthModal from "../components/AuthModal.js";
import matchScoreHeroImage from "./../assets/persian-rug-tee-image.jpg";
import modalImageOne from "./../assets/modal-image-one.png";
import modalImageTryOnTwo from "./../assets/modal-image-try-on-two.png";
import modalImageTryOn from "./../assets/modal-image-try-on.png";
import modalImageTryOnThree from "./../assets/modal-image-try-on-three.png";
import CatalogHeroImage from "./../assets/catalog-hero-image.png"
import Footer from "../components/Footer.js";
import SEO from "../components/SEO.js";
import HomeProfilePopupFlow from "./../components/HomeProfilePopupFlow.js";
import ProfileMeasurementsForm from "../components/ProfileMeasurementsForm.js";

const garmentTypeMap = {
  Dresses: "dresses",
  Tops: "tops",
  Pants: "pants",
  Outerwear: "outerwear",
  Casual: "casual",
  Work: "work",
  Evening: "evening",
  Summer: "summer",
};

const categoriesTypeMap = {
  Dresses: "dresses",
  Tops: "tops",
  Bottoms: "bottoms",
  Outerwear: "outerwear",
};

const sustainabilityTypeMap = {
  "Eco-Friendly": "eco_friendly",
  Organic: "organic",
  "Recycled Materials": "recycled_materials",
  Vegan: "vegan",
  "Fair Trade/Ethical": "fair_trade_ethical",
};

const sortTypeMap = {
  newest: "newest",
  popular: "popular",
  "price-asc": "price_asc",
  "price-desc": "price_desc",
};

const sizeTypeMap = {
  XS: "xs",
  S: "s",
  M: "m",
  L: "l",
  XL: "xl",
  XXL: "xxl",
};

const fitTypeMap = {
  Slim: "slim",
  Regular: "regular",
  Relaxed: "relaxed",
  Oversized: "oversized",
};

const occasionTypeMap = {
  Casual: "casual",
  Work: "work",
  Formal: "formal",
  Evening: "evening",
};

const colorTypeMap = {
  Black: "black",
  White: "white",
  Gray: "gray",
  Navy: "navy",
  Brown: "brown",
};

const imageHeightClasses = ["h-[310px]", "h-[340px]", "h-[370px]", "h-[400px]"];

const getProductImageHeightClass = (item, index) => {
  const source = String(item?.id ?? item?.name ?? index);

  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  const safeIndex = Math.abs(hash) % imageHeightClasses.length;

  return imageHeightClasses[safeIndex];
};

const getMarketplaceSlug = (product) => {
  return product?.real_slug || product?.slug || "";
};

const normalizeSignedInProducts = (response) => {
  const products = response?.products ?? [];

  return products.map((product) => ({
    id: product?.id,
    name: product?.product_name ?? "",
    price: product?.price ?? "",
    image: product?.image_url ?? "",
    url: product?.product_url ?? "",
    slug: getMarketplaceSlug(product),
    real_slug: product?.real_slug || product?.slug || "",
    brand: product?.brand ?? "",
    garmentType: product?.garment_type ?? "",
    matchPercentage: product?.match_percentage ?? null,
    perfectMatch: product?.perfect_match ?? false,
    productType: "marketplace",
    product_type: "marketplace",
    is_marketplace: true,
  }));
};

const normalizeGuestProducts = (response) => {
  const products = Array.isArray(response)
    ? response
    : Array.isArray(response?.products)
      ? response.products
      : [];

  return products.map((product) => ({
    id: product?.id,
    name: product?.product_name ?? "",
    price: product?.price ?? "",
    image: product?.image_url ?? "",
    url: product?.product_url ?? "",
    slug: getMarketplaceSlug(product),
    real_slug: product?.real_slug || product?.slug || "",
    brand: product?.brand ?? "",
    garmentType: product?.garment_type ?? "",
    matchPercentage: product?.match_percentage ?? null,
    perfectMatch: product?.perfect_match ?? false,
    productType: "marketplace",
    product_type: "marketplace",
    is_marketplace: true,
  }));
};

const normalizePublicSearchProducts = (response) => {
  const products = response?.products ?? [];

  return products.map((product) => ({
    id: product?.id,
    name: product?.name ?? "",
    price: product?.price ?? "",
    image: product?.image_url ?? "",
    url: product?.url ?? "",
    slug: getMarketplaceSlug(product),
    real_slug: product?.real_slug || product?.slug || "",
    brand: product?.brand ?? "",
    garmentType: product?.garment_type ?? "",
    matchPercentage: product?.match_percentage ?? null,
    perfectMatch: product?.perfect_match ?? false,
    productType: "marketplace",
    product_type: "marketplace",
    is_marketplace: true,
  }));
};
const normalizeSignedInSearchProducts = (response) => {
  const products = response?.products ?? [];

  return products.map((product) => ({
    id: product?.id,
    name: product?.name ?? "",
    price: product?.price ?? "",
    image: product?.image_url ?? "",
    url: product?.url ?? "",
    slug: getMarketplaceSlug(product),
    real_slug: product?.real_slug || product?.slug || "",
    brand: product?.brand ?? "",
    garmentType: product?.garment_type ?? "",
    matchPercentage: product?.match_percentage ?? null,
    perfectMatch: product?.perfect_match ?? false,
    productType: "marketplace",
    product_type: "marketplace",
    is_marketplace: true,
  }));
};
const useDebouncedValue = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const MAX_PRICE_STEP = 1000;

const defaultFilters = {
  categories: "",
  size: "",
  fit: "",
  occasion: "",
  color: "",
  minPrice: null,
  maxPrice: null,
};

const parseNumberParam = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const MARKETPLACE_PAGE_SIZE = 20;
const MARKETPLACE_CACHE_TTL_MS = 5 * 60 * 1000;
const MARKETPLACE_SEARCH_CACHE_KEY = "new_marketplace_search_signature";
const MARKETPLACE_SEARCH_ACTIVE_KEY = "new_marketplace_search_active";
const MARKETPLACE_SUGGESTIONS_CACHE_PREFIX =
  "new_marketplace_suggestions_cache";

const getSuggestionsCacheKey = (isAuthenticated) =>
  `${MARKETPLACE_SUGGESTIONS_CACHE_PREFIX}_${isAuthenticated ? "user" : "public"}`;

const readSuggestionsCache = (isAuthenticated) => {
  try {
    const cacheKey = getSuggestionsCacheKey(isAuthenticated);
    const cachedValue = sessionStorage.getItem(cacheKey);

    if (!cachedValue) return [];
    const cached = JSON.parse(cachedValue);
    const cachedAt = Number(cached?.cachedAt || 0);
    const isFresh = cachedAt > 0 && Date.now() - cachedAt < MARKETPLACE_CACHE_TTL_MS;

    if (!isFresh || !Array.isArray(cached?.products)) {
      sessionStorage.removeItem(cacheKey);
      return [];
    }

    return cached.products;
  } catch {
    return [];
  }
};

const writeSuggestionsCache = (isAuthenticated, products) => {
  try {
    const cacheKey = getSuggestionsCacheKey(isAuthenticated);

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        cachedAt: Date.now(),
        products,
      }),
    );
  } catch {}
};

const areFiltersEmpty = (filters = defaultFilters) => {
  return (
    !filters.categories &&
    !filters.size &&
    !filters.fit &&
    !filters.occasion &&
    !filters.color &&
    filters.minPrice === null &&
    (filters.maxPrice === null || filters.maxPrice === MAX_PRICE_STEP)
  );
};

const hasActiveSearchCriteria = ({
  text,
  selectedCategory,
  selectedSort,
  selectedAesthetic,
  selectedSustainability,
  appliedFilters,
}) => {
  return (
    Boolean(text?.trim()) ||
    Boolean(selectedCategory && selectedCategory !== "All") ||
    Boolean(selectedSort) ||
    Boolean(selectedAesthetic) ||
    Boolean(selectedSustainability) ||
    !areFiltersEmpty(appliedFilters)
  );
};

const buildSearchSignature = ({
  text,
  selectedCategory,
  selectedSort,
  selectedAesthetic,
  selectedSustainability,
  appliedFilters,
  isAuthenticated,
}) => {
  return JSON.stringify({
    auth: isAuthenticated ? "user" : "public",
    text: text || "",
    category: selectedCategory || "All",
    sort: selectedSort || "",
    aesthetic: selectedAesthetic || "",
    sustainability: selectedSustainability || "",
    filters: {
      categories: appliedFilters?.categories || "",
      size: appliedFilters?.size || "",
      fit: appliedFilters?.fit || "",
      occasion: appliedFilters?.occasion || "",
      color: appliedFilters?.color || "",
      minPrice: appliedFilters?.minPrice ?? null,
      maxPrice: appliedFilters?.maxPrice ?? null,
    },
  });
};
const hasSearchRelatedParams = (params) => {
  return [
    "q",
    "category",
    "sort",
    "aesthetic",
    "sustainability",
    "filterCategory",
    "size",
    "fit",
    "occasion",
    "color",
    "minPrice",
    "maxPrice",
  ].some((key) => params.has(key));
};


const guestHeroSlides = [
  {
    image: matchScoreHeroImage,
    alt: "Get your Eveness match score",
    surveyTitle: "Get Your Match Score on 100k+ Products",
    action: "match-score",
  },
  {
    image: modalImageOne,
    alt: "Try on outfits with Eveness",
    surveyTitle: "Try-On Your Favorite Pieces Together.",
    action: "try-on",
    detailImages: [
      modalImageTryOnTwo,
      modalImageTryOn,
      modalImageTryOnThree,
    ],
  },
  {
    image: CatalogHeroImage,
    alt: "Browse the personalized Eveness catalog",
    surveyTitle: "Upload Your Image to Access Eveness",
    action: "catalog",
  },
];

const isReactSnap =
  typeof navigator !== "undefined" &&
  navigator.userAgent === "ReactSnap";

const GuestHeroVisual = ({ slide }) => {
  if (!slide) return null;

  if (slide.action === "match-score") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#222]">
        {/* SAME IMAGE AS BLURRED BACKGROUND */}
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          className="
            absolute inset-0 h-full w-full
            scale-[1.15] object-cover
            blur-[18px] brightness-[0.5]
          "
        />

        <div className="absolute inset-0 bg-black/10" />

        {/* FOREGROUND COMPOSITION */}
        <div
          className="
            absolute left-1/2 top-1/2
            w-[86%] -translate-x-1/2 -translate-y-1/2
            md:left-[12%] md:w-auto md:translate-x-0
          "
        >
          <div className="relative">
            {/* PRODUCT CARD */}
            <div
              className="
                w-[230px] overflow-hidden rounded-[7px]
                bg-white/95
                sm:w-[270px]
                md:w-[290px]
                lg:w-[330px]
              "
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="
                  h-[220px] w-full object-cover
                  rounded-[12px] px-[6px] pt-[6px]
                  sm:h-[255px]
                  md:h-[290px]
                  lg:h-[310px]
                "
              />

              <div
                className="
                  flex h-[38px] items-center
                  px-[6px] text-[12px] font-normal text-black
                  md:text-[14px] font-helvena
                "
              >
                01 — PERSIAN RUG TEE
              </div>
            </div>

            {/* SCORE CARD */}
            <div
              className="
                absolute left-[70%] top-[45%]
                w-[150px]
                rounded-[8px]
                bg-white/90 px-4 py-4
                shadow-sm backdrop-blur-sm

                sm:left-[78%] sm:w-[165px]
                md:left-[80%] md:w-[175px]
                md:px-5 md:py-5
                lg:left-[82%]
              "
            >
              <div className="mb-5 text-center">
                <p className="mb-1 text-[13px] text-black md:text-[14px]">
                  Body shape
                </p>

                <div
                  className="
                    mx-auto flex h-[32px] w-[32px] font-bold
                    items-center justify-center rounded-full
                    border-2 border-[#00BC7D]
                    text-[12px] md:text-[14px] text-[#007A55]
                    font-spaceMono 
                  "
                >
                  82
                </div>
              </div>

              <div className="mb-5 text-center">
                <p className="mb-1 text-[13px] text-black md:text-[14px]">
                  Color Analysis
                </p>

                <div
                  className="
                    mx-auto flex h-[32px] w-[32px] font-bold
                    items-center justify-center rounded-full
                    border-2 border-[#00BC7D]
                    text-[12px] md:text-[14px] text-[#007A55]
                    font-spaceMono 
                  "
                >
                  88
                </div>
              </div>

              <div className="text-center">
                <p className="mb-1 text-[13px] text-black md:text-[14px]">
                  Neckline
                </p>

                <div
                  className="
                    mx-auto flex h-[32px] w-[32px] font-bold
                    items-center justify-center rounded-full
                    border-2 border-[#00BC7D]
                    text-[12px] md:text-[14px] text-[#007A55]
                    font-spaceMono 
                  "
                >
                  82
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (slide.action === "try-on") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#eee]">
        {/* SAME IMAGE AS BLURRED BACKGROUND */}
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          className="
            absolute inset-0 h-full w-full
            scale-[1.15] object-cover
            blur-[18px] brightness-[0.92]
          "
        />

        <div className="absolute inset-0 bg-white/10" />

        {/* FOREGROUND TRY-ON CARD */}
        <div
          className="
            absolute left-[8%] top-[54%]
            -translate-y-1/2

            md:left-[12%]
            lg:left-[16%]
          "
        >
          <div className="relative flex items-end gap-3">
            <div
              className="
                h-[280px] w-[230px]
                overflow-hidden rounded-[5px]
                bg-white shadow-sm

                sm:h-[320px] sm:w-[270px]
                md:h-[420px] md:w-[315px]
                lg:h-[440px] lg:w-[350px]
              "
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="h-full w-full object-cover"
              />
            </div>

            {/* SMALL TRY-ON IMAGES */}
            <div className="flex flex-col gap-2">
              <div
                className="
                  rounded-[7px] bg-white
                  px-3 py-2
                  text-[12px] md:text-[13px] text-[#424242]
                  shadow-sm
                "
              >
                Try-on everything!
              </div>

              {slide.detailImages?.map((image, index) => (
                <div
                  key={index}
                  className="
                    h-[58px] w-[58px]
                    overflow-hidden rounded-[5px]
                    bg-white shadow-sm

                    md:h-[82px] md:w-[82px]
                  "
                >
                  <img
                    src={image}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (slide.action === "catalog") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#333]">
        {/* NORMAL BACKGROUND */}
        <img
          src={slide.image}
          alt={slide.alt}
          className="
            absolute inset-0 h-full w-full
            object-cover brightness-[0.72]
          "
        />

        <div className="absolute inset-0 bg-black/10" />

        {/* OVERALL MATCH LEFT */}
        <div
          className="
            absolute left-[8%] top-[28%]
            rounded-[8px]
            bg-[#FFE5E766] px-5 py-4
            text-center backdrop-blur-sm
          "
        >
          <p className="text-[12px] text-black md:text-[14px]">
            Overall Match
          </p>

          <div
            className="
              mx-auto mt-2 flex h-[32px] w-[32px]
              items-center justify-center rounded-full
              border-2 border-white/70
              text-[11px] text-white
            "
          >
            87%
          </div>
        </div>

        {/* BEST MATCH */}
        <div
          className="
            absolute left-[45%] top-[20%]
            rounded-[8px]
            bg-white/50 px-5 py-4
            text-center backdrop-blur-sm
          "
        >
          <p className="text-[12px] text-black md:text-[14px]">
            Best Match
          </p>

          <div
            className="
              mx-auto font-spaceMono mt-1 flex h-[32px] w-[32px]
              items-center justify-center rounded-full font-bold
              border-2 border-[#00BC7D]
              text-[11px] md:text-[12px] text-[#007A55]
            "
          >
            91%
          </div>
        </div>

        {/* BOTTOM MATCH */}
        <div
          className="
            absolute bottom-[18%] left-[32%]
            rounded-[8px]
            bg-white/40 px-5 py-4
            text-center backdrop-blur-sm
          "
        >
          <p className="text-[12px] text-black md:text-[14px]">
            Overall Match
          </p>

          <div
            className="
              mx-auto font-spaceMono mt-1 flex h-[32px] w-[32px]
              items-center justify-center rounded-full font-bold
              border-2 border-[#BC9300]
              text-[11px] md:text-[12px] text-[#BC9300] bg-[#B2B0AB]
            "
          >
            70%
          </div>
        </div>

        {/* CATALOG CARD */}
        <div
          className="
            absolute left-[21%] top-[38%]
            w-[300px]
            rounded-[7px]
            bg-white/95 px-4 py-4
            shadow-sm

            md:w-[340px]
          "
        >
          <div className="text-[29px] leading-none text-[#555] font-helvena">
            Catalog
          </div>

          <div className="mt-[10px] text-[13px] text-[#8F333B] font-helvena ">
            100,000+ pieces narrowed to you.
          </div>

          <div
            className="
              mt-[10px] flex h-[30px]
              items-center justify-center
              rounded-[5px]
              bg-[#A6434699]
              font-spaceMono text-[9px] md:text-[12px]
              tracking-wide text-white
            "
          >
            See my edit
          </div>
        </div>

        <div
          className="
            absolute bottom-2 left-2
            text-[21px] font-bold uppercase
            text-white md:text-[24px]
          "
        >
          AI Fashion Marketplace
        </div>
      </div>
    );
  }

  return (
    <img
      src={slide.image}
      alt={slide.alt}
      className="h-full w-full object-cover"
    />
  );
};

const MobileGuestHeroVisual = ({ slide }) => {
  if (!slide) return null;

  if (slide.action === "match-score") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#ECE8E3]">
        {/* Full backdrop image */}
        <img
          src={slide.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center blur-[5px] brightness-[0.5]" />

      {/* Very light overlay to soften the backdrop */}
      <div className="absolute inset-0 bg-white/[0.04]" />
        {/* Product card */}
        <div className="absolute left-[7%] top-[20%] w-[65%] max-w-[280px] overflow-hidden rounded-[7px] bg-white/95 shadow-sm">
          <img
            src={slide.image}
            alt={slide.alt}
            className="aspect-[47/50] w-full rounded-t-[7px] object-cover p-[5px] pb-0" />
          <div
            className="flex h-[34px] items-center px-[7px] font-helvena text-[11px] font-normal text-black min-[390px]:text-[12px]">
            01 — PERSIAN RUG TEE
          </div>
        </div>

        {/* Score card */}
        <div
          className="absolute right-[2%] top-[39%] w-[40%] max-w-[166px] rounded-[8px] bg-white/95 px-3 py-4 shadow-sm backdrop-blur-sm min-[390px]:px-4">
          <div className="mb-4 text-center">
            <p className="mb-1 text-[11px] text-black min-[390px]:text-[13px]">
              Body shape
            </p>

            <div className="mx-auto flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-[#00BC7D] font-spaceMono text-[11px] font-bold text-[#007A55]">
              82
            </div>
          </div>

          <div className="mb-4 text-center">
            <p className="mb-1 text-[11px] text-black min-[390px]:text-[13px]">
              Color Analysis
            </p>

            <div
              className="mx-auto flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-[#00BC7D] font-spaceMono text-[11px] font-bold text-[#007A55]">
              88
            </div>
          </div>

          <div className="text-center">
            <p className="mb-1 text-[11px] text-black min-[390px]:text-[13px]">
              Neckline
            </p>

            <div className="mx-auto flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-[#00BC7D] font-spaceMono text-[11px] font-bold text-[#007A55]">
              82
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (slide.action === "try-on") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#FAFAFA]">
        {/* LEFT SIDE: main model */}
        <div className="
            absolute bottom-0 left-0
            h-full w-[62%]
          "
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="
              h-full w-full
              origin-bottom-left
              scale-[1.60]
              object-contain object-left-bottom
            "
          />
        </div>

        {/* RIGHT SIDE: title + supporting images */}
        <div
          className="
            absolute right-[4%] top-[10%]
            flex w-[28%] max-w-[140px]
            flex-col gap-[8px]
          "
        >
          {/* Title */}
          <div
            className="
              flex min-h-[40px]
              items-center justify-center
              rounded-[7px]
              bg-white/95
              px-2 py-2
              text-center
              font-helvena text-[10px]
              text-[#424242]
              shadow-sm
              min-[390px]:text-[11px]
            "
          >
            AI Try-on Anything!
          </div>

          {/* Supporting images */}
          {slide.detailImages?.map((image, index) => (
            <div
              key={index}
              className="
                aspect-square w-full
                overflow-hidden rounded-[6px]
                bg-white p-[3px]
                shadow-sm
              "
            >
              <img
                src={image}
                alt=""
                className="
                  h-full w-full
                  rounded-[4px]
                  object-cover object-center
                "
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (slide.action === "catalog") {
    return (
      <div className="relative h-full w-full overflow-hidden bg-[#333]">
        <img
          src={slide.image}
          alt={slide.alt}
          className="absolute inset-0 h-full w-full object-none object-center brightness-[0.8]" />

        <div className="absolute inset-0 bg-black/[0.06]" />

        {/* Upper match */}
        <div className="absolute left-[32%] top-[16%] w-[28%] rounded-[7px] bg-white/55 px-2 py-3 text-center backdrop-blur-sm" >
          <p className="text-[10px] text-black min-[390px]:text-[12px]">
            Overall Match
          </p>

          <div className="mx-auto mt-1 flex h-[29px] w-[29px] items-center justify-center rounded-full border-2 border-[#BC9300] font-spaceMono text-[10px] font-bold text-[#BC9300]" >
            70%
          </div>
        </div>

        {/* Best match */}
        <div className="absolute right-[10%] top-[47%] w-[24%] rounded-[7px] bg-white/55 px-2 py-3 text-center backdrop-blur-sm">
          <p className="text-[10px] text-black min-[390px]:text-[12px]">
            Best Match
          </p>

          <div className="
              mx-auto mt-1 flex h-[29px] w-[29px]
              items-center justify-center
              rounded-full border-2 border-[#00BC7D]
              font-spaceMono text-[10px] font-bold text-[#007A55]
            "
          >
            91%
          </div>
        </div>

        {/* Lower match */}
        <div className="absolute left-[4%] top-[61%] w-[28%] rounded-[7px] bg-white/40 px-2 py-3 text-center backdrop-blur-sm">
          <p className="text-[10px] text-black min-[390px]:text-[12px]">
            Overall Match
          </p>

          <div
            className="
              mx-auto mt-1 flex h-[29px] w-[29px]
              items-center justify-center
              rounded-full border-2 border-white/70
              font-spaceMono text-[10px] text-white
            "
          >
            87%
          </div>
        </div>

        {/* Catalog */}
        <div className="absolute bottom-[28px] left-[4%] w-[72%] max-w-[310px] rounded-[7px] bg-white/95 px-3 py-3 shadow-sm">
          <div className="font-helvena text-[27px] leading-none text-[#555]">
            Catalog
          </div>

          <div className="mt-2 font-helvena text-[11px] text-[#8F333B] min-[390px]:text-[12px]">
            100,000+ pieces narrowed to you.
          </div>

          <div
            className="
              mt-2 flex h-[27px] w-[72%]
              items-center justify-center
              rounded-[4px]
              bg-[#A6434699]
              font-spaceMono text-[8px]
              tracking-wide text-white
            "
          >
            See my edit
          </div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={slide.image}
      alt={slide.alt}
      className="h-full w-full object-cover"
    />
  );
};

const SurveyCarouselHero = ({
  slides,
  activeIndex,
  setActiveIndex,
  mobileRef,
  mobileTargetRef,
  onMobileScroll,
  onAnalyze,
  isAuthenticated,
  onInteraction,
  showSurvey,
}) => {
  const activeSlide = slides[activeIndex] ?? slides[0];

  const goToSlide = (index) => {
    setActiveIndex(index);

    const slider = mobileRef.current;

    if (slider?.clientWidth) {
      mobileTargetRef.current = index;

      slider.scrollTo({
        left: index * slider.clientWidth,
        behavior: "smooth",
      });
    }
  };

  const goToPreviousSlide = () => {
    onInteraction?.();
    setActiveIndex((currentIndex) =>
      currentIndex === 0 ? slides.length - 1 : currentIndex - 1
    );
  };

  const goToNextSlide = () => {
    onInteraction?.();
    setActiveIndex((currentIndex) =>
      currentIndex === slides.length - 1 ? 0 : currentIndex + 1
    );
  };

  return (
    <section className="relative mt-1 overflow-hidden rounded-[12px] bg-[#FAFAFA]">
      <div className="relative md:h-[600px] lg:h-[640px]">

        {/* HERO IMAGE AREA */}
        <div className="relative h-[clamp(480px,132vw,570px)] md:absolute md:inset-0 md:h-full">

          {/* Mobile carousel */}
          <div
            ref={mobileRef}
            onScroll={(event) =>
              onMobileScroll(
                event,
                setActiveIndex,
                slides.length,
                mobileTargetRef,
              )
            }
            onPointerDown={() => {
              mobileTargetRef.current = null;
              onInteraction?.();
            }}
            className="
              flex h-full snap-x snap-mandatory
              overflow-x-auto scroll-smooth
              md:hidden
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
            "
          >
            {slides.map((slide) => (
              <div
                key={slide.alt}
                className="h-full w-full shrink-0 snap-center overflow-hidden"
              >
                <MobileGuestHeroVisual slide={slide} />
              </div>
            ))}
          </div>

          {/* Desktop carousel */}
          <div className="relative hidden h-full overflow-hidden md:block">
            {/* Sliding track */}
            <div className="flex h-full transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
              }}
            >
              {slides.map((slide) => (
                <div
                  key={slide.alt}
                  className="h-full w-full shrink-0"
                >
                  <GuestHeroVisual slide={slide} />
                </div>
              ))}
            </div>

            <div className="pointer-events-none absolute inset-0 bg-black/[0.04]" />
            {/* Previous */}
            <button
              type="button"
              aria-label="Previous slide"
              onClick={goToPreviousSlide}
              className="
                absolute left-5 top-1/2 z-40
                flex h-10 w-10 -translate-y-1/2
                items-center justify-center
                rounded-full bg-white/80
                text-[24px] text-[#444]
                shadow-md backdrop-blur-sm
                transition pb-1
                hover:bg-white
              "
            >
              ‹
            </button>

            {/* Next */}
            <button
              type="button"
              aria-label="Next slide"
              onClick={goToNextSlide}
              className={`
                absolute top-1/2 z-40
                flex h-10 w-10 -translate-y-1/2
                items-center justify-center
                rounded-full
                bg-white/80
                text-[24px] text-[#444]
                shadow-md backdrop-blur-sm
                transition pb-1
                hover:bg-white right-5
              `}
            >
              ›
            </button>
          </div>

          {/* Carousel indicators */}
          <div
            className="
              absolute bottom-3 left-1/2 z-20
              flex -translate-x-1/2 items-center gap-[6px]
              md:bottom-5 md:left-[38%]
            "
          >
            {slides.map((slide, index) => (
              <button
                key={slide.alt}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => goToSlide(index)}
                className={`h-[6px] rounded-full transition-all duration-200 ${
                  index === activeIndex
                    ? "w-[24px] bg-white"
                    : "w-[6px] bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>

        {/* SURVEY CARD */}
        {showSurvey && (
        <div
          onPointerDown={() => onInteraction?.()}
          className="
            relative z-30 px-[8px] pb-[8px] pt-[8px]
            md:absolute md:right-[4%] md:top-[57%]
            md:w-[350px] md:-translate-y-1/2
            md:px-0 md:pb-0 md:pt-0
            lg:right-[6%] lg:w-[370px]
          "
        >
          <ProfileMeasurementsForm
            variant="hero"
            heroTitle={activeSlide?.surveyTitle}
            submitLabel="Start Eveness Survey"
            onAnalyze={onAnalyze}
            showSignInText={false}
            hideAuthActions
            isAuthenticated={isAuthenticated}
          />
        </div>
        )}
      </div>
    </section>
  );
};

const MarketplaceProductSkeletons = () => (  <section className="mt-4 grid grid-cols-2 items-start gap-3 md:grid-cols-3 xl:grid-cols-5">
    {Array.from({ length: 15 }).map((_, index) => (
      <div
        key={index}
        className="min-w-0"
      >
        <div className="animate-pulse overflow-hidden rounded-[12px] bg-white">
          <div
            className={`w-full rounded-[12px] bg-[#ECECEC] ${
              imageHeightClasses[index % imageHeightClasses.length]
            }`}
          />
          <div className="mt-3 space-y-2">
            <div className="h-3 w-3/4 rounded bg-[#ECECEC]" />
            <div className="h-3 w-1/2 rounded bg-[#ECECEC]" />
            <div className="h-3 w-1/3 rounded bg-[#ECECEC]" />
          </div>
        </div>
      </div>
    ))}
  </section>
);

const NewMarketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { closeChatModal } = useMarketplaceChat();
  const [advancedMeasurementsModalOpen, setAdvancedMeasurementsModalOpen] = useState(false);

  const initialSearchText = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("category") ?? "All";
  const initialSort = searchParams.get("sort") ?? "";
  const initialAesthetic = searchParams.get("aesthetic") ?? "";
  const initialSustainability = searchParams.get("sustainability") ?? "";
  const location = useLocation();
  const isMarketplacePage = location.pathname === "/marketplace";
  const [isHomeProfileFlowOpen, setIsHomeProfileFlowOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [autoStartGoogle, setAutoStartGoogle] = useState(false);
  const [authTick, setAuthTick] = useState(0);

  const initialAppliedFilters = {
    categories: searchParams.get("filterCategory") ?? "",
    size: searchParams.get("size") ?? "",
    fit: searchParams.get("fit") ?? "",
    occasion: searchParams.get("occasion") ?? "",
    color: searchParams.get("color") ?? "",
    minPrice: searchParams.has("minPrice")
      ? parseNumberParam(searchParams.get("minPrice"), 0)
      : null,
    maxPrice: searchParams.has("maxPrice")
      ? parseNumberParam(searchParams.get("maxPrice"), MAX_PRICE_STEP)
      : null,
  };

  const initialSearchSignature = buildSearchSignature({
    text: initialSearchText.trim(),
    selectedCategory: initialCategory,
    selectedSort: initialSort,
    selectedAesthetic: initialAesthetic,
    selectedSustainability: initialSustainability,
    appliedFilters: initialAppliedFilters,
    isAuthenticated: Boolean(localStorage.getItem("authToken")),
  });

  const initialHasActiveSearchCriteria = hasActiveSearchCriteria({
    text: initialSearchText,
    selectedCategory: initialCategory,
    selectedSort: initialSort,
    selectedAesthetic: initialAesthetic,
    selectedSustainability: initialSustainability,
    appliedFilters: initialAppliedFilters,
  });

  const shouldRestoreSearchMode =
    initialHasActiveSearchCriteria &&
    (Boolean(initialSearchText.trim()) ||
      hasSearchRelatedParams(searchParams) ||
      sessionStorage.getItem(MARKETPLACE_SEARCH_ACTIVE_KEY) === "true" ||
      sessionStorage.getItem(MARKETPLACE_SEARCH_CACHE_KEY) ===
        initialSearchSignature);

  const [searchText, setSearchText] = useState(initialSearchText);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSort, setSelectedSort] = useState(initialSort);
  const [selectedAesthetic, setSelectedAesthetic] = useState(initialAesthetic);
  const [selectedSustainability, setSelectedSustainability] = useState(
    initialSustainability,
  );
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const [cachedSuggestionProducts, setCachedSuggestionProducts] = useState(
    () => readSuggestionsCache(Boolean(localStorage.getItem("authToken"))),
  );
  const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);
  const [draftFilters, setDraftFilters] = useState(initialAppliedFilters);
  const [activeGuestHeroSlide, setActiveGuestHeroSlide] = useState(0);
  const [isSurveyHeroPaused, setIsSurveyHeroPaused] = useState(false);
  const [homeProfileFlowConfig, setHomeProfileFlowConfig] = useState({
    initialStep: 0,
    initialProfile: null,
    autoAnalyzeOnOpen: false,
  });

  const mobileGuestHeroRef = useRef(null);
  const mobileGuestHeroTargetSlideRef = useRef(null);
  const [hasTriggeredSearch, setHasTriggeredSearch] = useState(
    shouldRestoreSearchMode,
  );
  const authToken = useMemo(() => {
    return localStorage.getItem("authToken");
  }, [authTick]);

  const isAuthenticated = Boolean(authToken);

  const hasPendingSurveyToken = useMemo(
    () => Boolean(getPendingSurveyToken()),
    [authTick],
  );

  const {
    data: profileData,
    isSuccess: isProfileSuccess,
    isError: isProfileError,
    error: profileError,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfile,
  } = useProfileReadQuery(undefined, {
    skip: !isAuthenticated,
  });

  const profileMissing =
  isAuthenticated &&
  isProfileError &&
  (
    profileError?.status === 404 ||
    profileError?.originalStatus === 404
  );

  const hasCompletedSurvey =
    isAuthenticated && isProfileSuccess && Boolean(profileData);

  const shouldUseSignedMarketplaceApis = hasCompletedSurvey;

  const shouldShowSurveyPrompt =
    !hasPendingSurveyToken &&
    (
      !isAuthenticated ||
      profileMissing
    );

  const canUseRealChat = hasCompletedSurvey;

  useEffect(() => {
    if (
      hasCompletedSurvey ||
      hasPendingSurveyToken
    ) {
      setIsHomeProfileFlowOpen(false);
    }
  }, [
    hasCompletedSurvey,
    hasPendingSurveyToken,
  ]);

  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);

  const isProfileChecking =
    isAuthenticated && (isProfileLoading || isProfileFetching);

  const [searchRequestNonce, setSearchRequestNonce] = useState(0);
  const lastExecutedSearchRef = useRef({
    signature: "",
    nonce: -1,
  });

  const hasHydratedCachedSearchRef = useRef(false);
  const debouncedSearchText = useDebouncedValue(searchText, 500);
  const trimmedDebouncedSearch = debouncedSearchText.trim();

  useEffect(() => {
    const handleAuthChanged = () => {
      setAuthTick((prev) => prev + 1);
    };

    const handleLoginCompleted = (event) => {
      setAuthTick((prev) => prev + 1);
      setIsAuthModalOpen(false);
      setAutoStartGoogle(false);

      const {
        claimFailed,
        surveyClaimed,
      } = event?.detail || {};


      const shouldOpenAdvancedMeasurementsModal =
        surveyClaimed === true &&
        claimFailed !== true;

      if (shouldOpenAdvancedMeasurementsModal) {
        setAdvancedMeasurementsModalOpen(true);
      }

    };

    window.addEventListener("eveness-auth-changed", handleAuthChanged);
    window.addEventListener("eveness-login-completed", handleLoginCompleted);

    return () => {
      window.removeEventListener("eveness-auth-changed", handleAuthChanged);
      window.removeEventListener(
        "eveness-login-completed",
        handleLoginCompleted,
      );
    };
  }, []);

  const openAuthModal = (mode = "login", options = {}) => {
    const { startGoogle = false } = options;

    setAuthModalMode(mode);
    setAutoStartGoogle(startGoogle);
    setIsAuthModalOpen(true);
  };

  const openLoginModal = () => {
    openAuthModal("login");
  };

  const handleCloseAuthModal = () => {
    setIsAuthModalOpen(false);
    setAutoStartGoogle(false);
  };

  const handleCloseHomeProfileFlow = () => {
    setIsHomeProfileFlowOpen(false);
    setHomeProfileFlowConfig({
      initialStep: 0,
      initialProfile: null,
      autoAnalyzeOnOpen: false,
    });
    setAuthTick((prev) => prev + 1);
  };

  const handleHomeProfileSignIn = () => {
    setIsHomeProfileFlowOpen(false);
    openAuthModal("login");
  };

  const handleHomeProfileSignUp = ({ provider, profile }) => {
    console.log("Home profile data:", profile);

    if (provider === "google") {
      setAuthModalMode("signup");
      setAutoStartGoogle(true);
      setIsAuthModalOpen(true);
      return;
    }

    setIsHomeProfileFlowOpen(false);
    setAutoStartGoogle(false);
    setAuthModalMode("signup");
    setIsAuthModalOpen(true);
  };

  const openSurveyGate = () => {
    closeChatModal();

    if (!isAuthenticated) {
      openLoginModal();
      return;
    }

    if (isProfileChecking) {
      toast.loading("Checking your profile...");
      return;
    }

    if (hasPendingSurveyToken) {
      toast(
        "Your saved profile is being updated. Please wait a moment.",
      );
      return;
    }

    if (!hasCompletedSurvey && profileMissing) {
      setIsHomeProfileFlowOpen(true);
    }
  };

  const searchSignature = useMemo(
    () =>
      buildSearchSignature({
        text: trimmedDebouncedSearch,
        selectedCategory,
        selectedSort,
        selectedAesthetic,
        selectedSustainability,
        appliedFilters,
        isAuthenticated: shouldUseSignedMarketplaceApis,
      }),
    [
      trimmedDebouncedSearch,
      selectedCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
      shouldUseSignedMarketplaceApis,
    ],
  );

  const hasRealActiveSearchCriteria = useMemo(
    () =>
      hasActiveSearchCriteria({
        text: trimmedDebouncedSearch,
        selectedCategory,
        selectedSort,
        selectedAesthetic,
        selectedSustainability,
        appliedFilters,
      }),
    [
      trimmedDebouncedSearch,
      selectedCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
    ],
  );

  const activeGarmentType =
    selectedCategory === "All" ? undefined : garmentTypeMap[selectedCategory];

  const isSearching = hasTriggeredSearch && hasRealActiveSearchCriteria;
  const marketplaceListingMode = isSearching
    ? shouldUseSignedMarketplaceApis
      ? "user-search"
      : "public-search"
    : shouldUseSignedMarketplaceApis
      ? "user-suggestions"
      : "public-suggestions";

  const suggestionsOwnerKey = authToken ? `token-${String(authToken).slice(-12)}` : "guest";

  const marketplaceListingRequestKey =
    marketplaceListingMode === "public-search"
      ? `${marketplaceListingMode}:${searchSignature}`
      : marketplaceListingMode === "user-search"
        ? `${marketplaceListingMode}:${suggestionsOwnerKey}:${searchSignature}`
        : marketplaceListingMode === "user-suggestions"
          ? `${marketplaceListingMode}:${suggestionsOwnerKey}`
          : marketplaceListingMode;

  const [marketplacePagination, setMarketplacePagination] = useState({
    requestKey: marketplaceListingRequestKey,
    page: 1,
  });

  const marketplacePage = marketplacePagination.requestKey === marketplaceListingRequestKey
      ? marketplacePagination.page
      : 1;

  const marketplaceLoadMoreRef = useRef(null);

  useEffect(() => {
    setMarketplacePagination({
      requestKey: marketplaceListingRequestKey,
      page: 1,
    });
  }, [marketplaceListingRequestKey]);

  const resetSearchModeIfNoCriteria = (nextState) => {
    const nextHasActiveCriteria = hasActiveSearchCriteria({
      text: nextState.text,
      selectedCategory: nextState.selectedCategory,
      selectedSort: nextState.selectedSort,
      selectedAesthetic: nextState.selectedAesthetic,
      selectedSustainability: nextState.selectedSustainability,
      appliedFilters: nextState.appliedFilters,
    });

    hasHydratedCachedSearchRef.current = false;

    if (!nextHasActiveCriteria) {
      setHasTriggeredSearch(false);
      sessionStorage.removeItem(MARKETPLACE_SEARCH_ACTIVE_KEY);

      lastExecutedSearchRef.current = {
        signature: "",
        nonce: -1,
      };

      return false;
    }

    setHasTriggeredSearch(true);
    return true;
  };

  const handleCategoryChange = (value) => {
    const nextCategory = value || "All";

    setSelectedCategory(nextCategory);

    resetSearchModeIfNoCriteria({
      text: searchText,
      selectedCategory: nextCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
    });
  };

  const handleSortChange = (value) => {
    const nextSort = value || "";

    setSelectedSort(nextSort);

    resetSearchModeIfNoCriteria({
      text: searchText,
      selectedCategory,
      selectedSort: nextSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
    });
  };

  const handleAestheticChange = (value) => {
    const nextAesthetic = value || "";

    setSelectedAesthetic(nextAesthetic);

    resetSearchModeIfNoCriteria({
      text: searchText,
      selectedCategory,
      selectedSort,
      selectedAesthetic: nextAesthetic,
      selectedSustainability,
      appliedFilters,
    });
  };

  const handleSustainabilityChange = (value) => {
    const nextSustainability = value || "";

    setSelectedSustainability(nextSustainability);

    resetSearchModeIfNoCriteria({
      text: searchText,
      selectedCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability: nextSustainability,
      appliedFilters,
    });
  };

  const handleSearchTextChange = (value) => {
    setSearchText(value);

    resetSearchModeIfNoCriteria({
      text: value,
      selectedCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
    });
  };

  const handleDraftFiltersChange = (updater) => {
    setDraftFilters((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  };

  const marketplaceSearchQueryArg = useMemo(  () => ({
      text: trimmedDebouncedSearch,
      page: marketplacePage,
      page_size: MARKETPLACE_PAGE_SIZE,
      garmentType: activeGarmentType,
      sort: selectedSort
        ? sortTypeMap[selectedSort]
        : undefined,
      aesthetic: selectedAesthetic || undefined,
      sustainability: selectedSustainability
        ? sustainabilityTypeMap[selectedSustainability]
        : undefined,
      categoriesType: appliedFilters.categories
        ? categoriesTypeMap[appliedFilters.categories]
        : undefined,
      sizeType: appliedFilters.size
        ? sizeTypeMap[appliedFilters.size]
        : undefined,
      fitType: appliedFilters.fit
        ? fitTypeMap[appliedFilters.fit]
        : undefined,
      occasionType: appliedFilters.occasion
        ? occasionTypeMap[appliedFilters.occasion]
        : undefined,
      color: appliedFilters.color
        ? colorTypeMap[appliedFilters.color]
        : undefined,
      minPrice:
        appliedFilters.minPrice !== null
          ? appliedFilters.minPrice
          : undefined,
      maxPrice:
        appliedFilters.maxPrice !== null &&
        appliedFilters.maxPrice !== MAX_PRICE_STEP
          ? appliedFilters.maxPrice
          : undefined,
    }),
    [
      trimmedDebouncedSearch,
      marketplacePage,
      activeGarmentType,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters,
    ],
  );

  const {
    currentData: publicSearchResponse,
    isLoading: isPublicSearchLoading,
    isFetching: isPublicSearchFetching,
    isError: isPublicSearchError,
  } = usePublicMarketplaceSearchCachedQuery(marketplaceSearchQueryArg,  {
      skip: marketplaceListingMode !== "public-search",
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    currentData: userSearchResponse,
    isLoading: isUserSearchLoading,
    isFetching: isUserSearchFetching,
    isError: isUserSearchError,
  } = useUserMarketplaceSearchCachedQuery(
      {
        ...marketplaceSearchQueryArg,
        owner_key: suggestionsOwnerKey,
      },
      {
        skip: marketplaceListingMode !== "user-search",
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false,
      },
    );

  const {
    data: signedInSuggestions,
    isLoading: isSignedInSuggestionsLoading,
    isFetching: isSignedInSuggestionsFetching,
    isError: isSignedInSuggestionsError,
  } = useUserMarketplaceSuggestionsCachedQuery(
    {
      page: marketplacePage,
      page_size: MARKETPLACE_PAGE_SIZE,
      owner_key: suggestionsOwnerKey,
    },
    {
      skip: marketplaceListingMode !== "user-suggestions",
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    data: publicSuggestions,
    isLoading: isPublicSuggestionsLoading,
    isFetching: isPublicSuggestionsFetching,
    isError: isPublicSuggestionsError,
  } = usePublicMarketplaceSuggestionsCachedQuery(undefined, {
    skip: marketplaceListingMode !== "public-suggestions",
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const publicSearchProducts = useMemo(
    () => normalizePublicSearchProducts(publicSearchResponse),
    [publicSearchResponse],
  );

  const userSearchProducts = useMemo(
    () => normalizeSignedInSearchProducts(userSearchResponse),
    [userSearchResponse],
  );

  const allPublicSuggestionProducts = useMemo(
    () => normalizeGuestProducts(publicSuggestions),
    [publicSuggestions],
  );

  const suggestionProducts = useMemo(() => {
    if (shouldUseSignedMarketplaceApis) {
      return normalizeSignedInProducts(signedInSuggestions);
    }

    return allPublicSuggestionProducts.slice(0,marketplacePage * MARKETPLACE_PAGE_SIZE);
  }, [shouldUseSignedMarketplaceApis, signedInSuggestions, allPublicSuggestionProducts, marketplacePage]);

  useEffect(() => {
    if (!suggestionProducts.length) return;

    writeSuggestionsCache(shouldUseSignedMarketplaceApis, suggestionProducts);
    setCachedSuggestionProducts(suggestionProducts);
  }, [suggestionProducts, shouldUseSignedMarketplaceApis]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);

  useEffect(() => {
    if (!isSearching) return;
    const activeSearchResponse = shouldUseSignedMarketplaceApis ? userSearchResponse : publicSearchResponse;
    if (!activeSearchResponse) return;

    hasHydratedCachedSearchRef.current = true;

    lastExecutedSearchRef.current = {
      signature: searchSignature,
      nonce: searchRequestNonce,
    };
    sessionStorage.setItem(MARKETPLACE_SEARCH_CACHE_KEY,searchSignature);

    sessionStorage.setItem(MARKETPLACE_SEARCH_ACTIVE_KEY, "true");
  }, [
    isSearching,
    shouldUseSignedMarketplaceApis,
    userSearchResponse,
    publicSearchResponse,
    searchSignature,
    searchRequestNonce,
  ]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (searchText.trim()) {
      nextParams.set("q", searchText);
    }

    if (selectedCategory && selectedCategory !== "All") {
      nextParams.set("category", selectedCategory);
    }

    if (selectedSort) {
      nextParams.set("sort", selectedSort);
    }

    if (selectedAesthetic) {
      nextParams.set("aesthetic", selectedAesthetic);
    }

    if (selectedSustainability) {
      nextParams.set("sustainability", selectedSustainability);
    }

    if (appliedFilters.categories) {
      nextParams.set("filterCategory", appliedFilters.categories);
    }

    if (appliedFilters.size) {
      nextParams.set("size", appliedFilters.size);
    }

    if (appliedFilters.fit) {
      nextParams.set("fit", appliedFilters.fit);
    }

    if (appliedFilters.occasion) {
      nextParams.set("occasion", appliedFilters.occasion);
    }

    if (appliedFilters.color) {
      nextParams.set("color", appliedFilters.color);
    }

    if (appliedFilters.minPrice !== null) {
      nextParams.set("minPrice", String(appliedFilters.minPrice));
    }

    if (
      appliedFilters.maxPrice !== null &&
      appliedFilters.maxPrice !== MAX_PRICE_STEP
    ) {
      nextParams.set("maxPrice", String(appliedFilters.maxPrice));
    }

    const currentParams = searchParams.toString();
    const newParams = nextParams.toString();

    if (currentParams !== newParams) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    searchText,
    selectedCategory,
    selectedSort,
    selectedAesthetic,
    selectedSustainability,
    appliedFilters,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isReactSnap ||
      !hasHydrated ||
      isProfileChecking ||
      isSurveyHeroPaused ||
      hasCompletedSurvey
    ) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveGuestHeroSlide((prev) => {
        const nextSlide = (prev + 1) % guestHeroSlides.length;
        const slider = mobileGuestHeroRef.current;
        if (slider) {
          mobileGuestHeroTargetSlideRef.current = nextSlide;

          slider.scrollTo({
            left: nextSlide * slider.clientWidth,
            behavior: "smooth",
          });
        }
        return nextSlide;
      });
    }, 4500);

    return () => {
      window.clearInterval(timer);
    };
  }, [
    hasHydrated,
    isProfileChecking,
    isSurveyHeroPaused,
    hasCompletedSurvey,
  ]);

  const handleMobileSliderScroll = (
    event,
    setActiveIndex,
    slideCount,
    programmaticTargetRef,
  ) => {
    const slider = event.currentTarget;
    const { scrollLeft, clientWidth } = slider;

    if (!clientWidth) return;

    const programmaticTarget = programmaticTargetRef.current;

    if (programmaticTarget !== null) {
      const targetScrollLeft = programmaticTarget * clientWidth;
      const hasReachedTarget =
        Math.abs(scrollLeft - targetScrollLeft) <= 2;

      if (hasReachedTarget) {
        programmaticTargetRef.current = null;
      }

      return;
    }

    const nextIndex = Math.min(
      slideCount - 1,
      Math.max(0, Math.round(scrollLeft / clientWidth)),
    );

    setActiveIndex((currentIndex) =>
      currentIndex === nextIndex ? currentIndex : nextIndex,
    );
  };

  const isWaitingForSignedInSuggestions =
    !isSearching &&
    shouldUseSignedMarketplaceApis &&
    (isSignedInSuggestionsLoading || isSignedInSuggestionsFetching) &&
    suggestionProducts.length === 0;

  const displaySuggestionProducts =
    isWaitingForSignedInSuggestions
      ? []
      : suggestionProducts.length > 0
        ? suggestionProducts
        : cachedSuggestionProducts;

  const products = isSearching
    ? shouldUseSignedMarketplaceApis
      ? userSearchProducts
      : publicSearchProducts
    : displaySuggestionProducts;

  const isSuggestionsLoading = shouldUseSignedMarketplaceApis
    ? isSignedInSuggestionsLoading || isSignedInSuggestionsFetching
    : isPublicSuggestionsLoading || isPublicSuggestionsFetching;

  const isSuggestionsError = shouldUseSignedMarketplaceApis
    ? isSignedInSuggestionsError
    : isPublicSuggestionsError;

  const activeSearchResponse = shouldUseSignedMarketplaceApis
    ? userSearchResponse
    : publicSearchResponse;

  const activeSearchIsLoading = shouldUseSignedMarketplaceApis ? isUserSearchLoading
    : isPublicSearchLoading;

  const activeSearchIsFetching = shouldUseSignedMarketplaceApis
    ? isUserSearchFetching
    : isPublicSearchFetching;

  const isSearchLoading = isSearching &&
    (activeSearchIsLoading || (activeSearchIsFetching && !activeSearchResponse));
  const isSearchApiError = shouldUseSignedMarketplaceApis
    ? isUserSearchError
    : isPublicSearchError;

  const hasSuggestionCache = displaySuggestionProducts.length > 0;

  const isLoading = isSearching
    ? isSearchLoading
    : isWaitingForSignedInSuggestions ||
      (isSuggestionsLoading && !hasSuggestionCache);
  const isError = isSearching
    ? isSearchApiError && products.length === 0
    : isSuggestionsError && !hasSuggestionCache;

  const activeServerPaginationResponse =
    marketplaceListingMode === "public-search"
      ? publicSearchResponse
      : marketplaceListingMode === "user-search"
        ? userSearchResponse
        : marketplaceListingMode === "user-suggestions"
          ? signedInSuggestions
          : null;

  const activeServerProducts = Array.isArray(activeServerPaginationResponse?.products,)
    ? activeServerPaginationResponse.products
    : [];

  const activeServerTotal = Number(activeServerPaginationResponse?.total ?? activeServerPaginationResponse?.total_products ?? 0);

  const serverHasMore =
    typeof activeServerPaginationResponse?.has_more === "boolean"
      ? activeServerPaginationResponse.has_more
      : activeServerTotal > activeServerProducts.length;

  const publicSuggestionsHasMore =
    marketplaceListingMode === "public-suggestions" &&
    suggestionProducts.length <
      allPublicSuggestionProducts.length;

  const canLoadMoreMarketplaceProducts =
    !isSearchLoading &&
    (
      marketplaceListingMode === "public-suggestions"
        ? publicSuggestionsHasMore
        : marketplaceListingMode === "public-search" ||
            marketplaceListingMode === "user-search" ||
            marketplaceListingMode === "user-suggestions"
          ? serverHasMore
          : false
    );

  const marketplaceNextPage =
    marketplaceListingMode === "public-suggestions"
      ? marketplacePage + 1
      : activeServerPaginationResponse?.next_page !== null &&
          activeServerPaginationResponse?.next_page !== undefined
        ? Number(activeServerPaginationResponse.next_page)
        : null;

  const isLoadingMoreMarketplaceProducts =
    !isSearchLoading &&
    marketplacePage > 1 &&
    Boolean(activeServerPaginationResponse) &&
    (
      (
        marketplaceListingMode === "public-search" &&
        isPublicSearchFetching
      ) ||
      (
        marketplaceListingMode === "user-search" &&
        isUserSearchFetching
      ) ||
      (
        marketplaceListingMode === "user-suggestions" &&
        isSignedInSuggestionsFetching
      )
    );

  useEffect(() => {
    if (isSearchLoading) return;
    if (!canLoadMoreMarketplaceProducts) return;
    if (isLoadingMoreMarketplaceProducts) return;
    if (products.length === 0) return;
      if (!Number.isFinite(marketplaceNextPage)) return;
      if (marketplaceNextPage <= marketplacePage) return;

      if (typeof IntersectionObserver === "undefined") {
        return;
      }

      const loadMoreNode = marketplaceLoadMoreRef.current;

      if (!loadMoreNode) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;

          setMarketplacePagination((current) => {
            const currentPage =
              current.requestKey === marketplaceListingRequestKey
                ? current.page
                : 1;

            if (marketplaceNextPage <= currentPage) {
              return current;
            }

            return {
              requestKey: marketplaceListingRequestKey,
              page: marketplaceNextPage,
            };
          });
        },
        {
          root: null,
          rootMargin: "500px 0px",
          threshold: 0.01,
        },
      );
      observer.observe(loadMoreNode);
      return () => {
        observer.disconnect();
      };
  }, [
    isSearchLoading,
    canLoadMoreMarketplaceProducts,
    isLoadingMoreMarketplaceProducts,
    products.length,
    marketplaceNextPage,
    marketplacePage,
    marketplaceListingRequestKey,
  ]);
  const handleApplyFilters = () => {
    const nextAppliedFilters = { ...draftFilters };

    const nextHasActiveCriteria = hasActiveSearchCriteria({
      text: searchText,
      selectedCategory,
      selectedSort,
      selectedAesthetic,
      selectedSustainability,
      appliedFilters: nextAppliedFilters,
    });

    hasHydratedCachedSearchRef.current = false;
    setMarketplacePagination((current) => ({ ...current,page: 1 }));
    setAppliedFilters(nextAppliedFilters);

    if (!nextHasActiveCriteria) {
      setHasTriggeredSearch(false);

      sessionStorage.removeItem(MARKETPLACE_SEARCH_CACHE_KEY);
      sessionStorage.removeItem(MARKETPLACE_SEARCH_ACTIVE_KEY);

      lastExecutedSearchRef.current = {
        signature: "",
        nonce: -1,
      };

      return;
    }

    setHasTriggeredSearch(true);
    setSearchRequestNonce((prev) => prev + 1);
  };

  const handleResetFilters = () => {
    hasHydratedCachedSearchRef.current = false;

    sessionStorage.removeItem(MARKETPLACE_SEARCH_CACHE_KEY);
    sessionStorage.removeItem(MARKETPLACE_SEARCH_ACTIVE_KEY);

    lastExecutedSearchRef.current = {
      signature: "",
      nonce: -1,
    };

    setSearchText("");
    setSelectedCategory("All");
    setSelectedSort("");
    setSelectedAesthetic("");
    setSelectedSustainability("");

    setHasTriggeredSearch(false);
    setDraftFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };
  const handleSaveBoard = (item) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to save this product.");
      openLoginModal();
      return;
    }

    if (!item) {
      toast.error("Product details are not available yet.");
      return;
    }

    const itemSlug = item?.real_slug || item?.slug || "";

    if (!itemSlug) {
      toast.error("Product slug is not available yet.");
      return;
    }

  const productType =
    item?.productType ||
    item?.product_type ||
    (item?.is_marketplace === false ? "product" : "marketplace");

  const visibleMatchPercentage = getVisibleCardMatchPercentage(item);

  setSaveBoardProduct({
    ...item,
    slug: itemSlug,
    real_slug: itemSlug,
    productType,
    product_type: productType,
    matchPercentage: visibleMatchPercentage,
    match_percentage: visibleMatchPercentage,
  });

  setIsSaveBoardModalOpen(true);
  };

  const handleHomeProfileRestartComplete = async () => {
    setIsHomeProfileFlowOpen(false);

    if (isAuthenticated) {
      await refetchProfile?.();
    }

    setAuthTick((prev) => prev + 1);
    setCachedSuggestionProducts([]);
    hasHydratedCachedSearchRef.current = false;

    if (hasTriggeredSearch && hasRealActiveSearchCriteria) {
      setSearchRequestNonce((prev) => prev + 1);
    }

    window.dispatchEvent(new Event("eveness-auth-changed"));
  };

  const handleHeroSurveyAnalyze = (profile) => {
    closeChatModal();
    setIsAuthModalOpen(false);
    setIsSurveyHeroPaused(true);
    setHomeProfileFlowConfig({
      initialStep: 3,
      initialProfile: profile,
      autoAnalyzeOnOpen: true,
    });
    setIsHomeProfileFlowOpen(true);
  };

const handleGuestHeroAction = () => {
  if (hasCompletedSurvey) {
    return;
  }

  closeChatModal();
  setIsAuthModalOpen(false);
  setHomeProfileFlowConfig({
    initialStep: 0,
    initialProfile: null,
    autoAnalyzeOnOpen: false,
  });
  setIsHomeProfileFlowOpen(true);
};
  return (
    <>
    <SEO
      title={"Eveness | Smarter AI Outfit Recommendations for Every Style"}
      description={"Shop smarter with Eveness. Discover AI outfit recommendations, personalized fashion suggestions and try before you buy clothes confidence for every style."}
      path={isMarketplacePage ? "/marketplace" : "/"}
    />
    <div className="min-h-screen bg-white font-sans text-slate-900">
  {hasHydrated && (
    <>
      <HomeProfilePopupFlow
        isOpen={isHomeProfileFlowOpen}
        onClose={handleCloseHomeProfileFlow}
        onSignIn={isAuthenticated ? undefined : handleHomeProfileSignIn}
        onSignUp={isAuthenticated ? undefined : handleHomeProfileSignUp}
        isGoogleRedirecting={autoStartGoogle}
        mode={isAuthenticated ? "restart" : "public"}
        onRestartComplete={handleHomeProfileRestartComplete}
        isAuthenticated={isAuthenticated}
        initialStep={homeProfileFlowConfig.initialStep}
        initialProfile={homeProfileFlowConfig.initialProfile}
        autoAnalyzeOnOpen={homeProfileFlowConfig.autoAnalyzeOnOpen}
      />
      <AuthModal
        isOpen={isAuthModalOpen}
        initialMode={authModalMode}
        autoStartGoogle={autoStartGoogle}
        preloadGoogle={isHomeProfileFlowOpen && !isAuthenticated}
        onGoogleStartConsumed={() => setAutoStartGoogle(false)}
        onClose={handleCloseAuthModal}
      />
    </>
  )}
      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
        }}
        onRequireAuth={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
          openLoginModal();
        }}
      />
      <NewNavbar
        overlayOnContent={true}
        mobileInFlow={true}
        bottomNav="marketplace"
        searchText={searchText}
        onSearchTextChange={handleSearchTextChange}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        selectedSort={selectedSort}
        onSortChange={handleSortChange}
        selectedAesthetic={selectedAesthetic}
        onAestheticChange={handleAestheticChange}
        selectedSustainability={selectedSustainability}
        onSustainabilityChange={handleSustainabilityChange}
        draftFilters={draftFilters}
        onDraftFiltersChange={handleDraftFiltersChange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
      <main className={`mx-auto max-w-[1480px] px-4 pb-[20px] md:px-6 ${
          hasCompletedSurvey
            ? "pt-4 lg:pt-[150px]"
            : "pt-4"
        }`}>{isAuthenticated && isProfileChecking ? (
          <section
            className="
              relative mt-1 flex h-[520px] items-center justify-center
              overflow-hidden rounded-[12px] bg-[#F3F1EF]
              md:h-[600px]
            "
          >
            <div className="flex items-center gap-2">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#D6D3D1] border-t-[#555555]" />
              <span className="font-spaceMono text-[11px] text-[#777777]">
                Loading your profile...
              </span>
            </div>
          </section>
        ) : !hasCompletedSurvey ? (
          <SurveyCarouselHero
            slides={guestHeroSlides}
            activeIndex={activeGuestHeroSlide}
            setActiveIndex={setActiveGuestHeroSlide}
            mobileRef={mobileGuestHeroRef}
            mobileTargetRef={mobileGuestHeroTargetSlideRef}
            onMobileScroll={handleMobileSliderScroll}
            onAnalyze={handleHeroSurveyAnalyze}
            isAuthenticated={isAuthenticated}
            onInteraction={() => setIsSurveyHeroPaused(true)}
            showSurvey={shouldShowSurveyPrompt}
          />
      ) : null}     
      <section
        id="marketplace-products"
        className="mt-5 scroll-mt-24"
      >
          <h2 className="m-0 font-inter text-[20px] font-bold text-[#14141F]">
            {isSearching
              ? "Search Results"
              : ""}
          </h2>

          <p className="mb-0 mt-1 font-spaceMono text-[11px] font-normal text-black">
            {isSearching
              ? `Showing results for the search filters`
              : "100000+ products, curated just for you"}
          </p>
        </section>
        {isLoading ? (
        isWaitingForSignedInSuggestions ? (
          <MarketplaceProductSkeletons />
        ) : (
          <section className="mt-4 py-10">
            <div className="flex items-center justify-center gap-2">
              <p className="font-inter text-[14px] text-[#80808C]">
                Loading products...
              </p>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#D5D7DE] border-t-[#80808C]" />
            </div>
          </section>
        )
        ) : isError ? (
          <section className="mt-4 py-10 text-center">
            <p className="font-inter text-[14px] text-[#80808C]">
              Failed to load products.
            </p>
          </section>
        ) : products.length === 0 ? (
          <section className="mt-4 py-10 text-center">
            <p className="font-inter text-[14px] text-[#80808C]">
              No products found.
            </p>
          </section>
        ) : (
          <>
              <StableMasonryGrid
                items={products}
                className="mt-4"
                keyPrefix="marketplace-product"
                mobileColumns={2}
                tabletColumns={3}
                desktopColumns={5}
                wideColumns={5}
                renderItem={(item, idx) => (
                  <ProductCard
                    item={item}
                    imageHeightClass={getProductImageHeightClass(item, idx)}
                    matchPercentage={item.matchPercentage}
                    onSaveBoard={handleSaveBoard}
                    isSavingBoard={false}
                    compactActions={idx < 5}
                    priorityImage={idx < 5}
                  />
                )}
              />
              {(canLoadMoreMarketplaceProducts ||
                isLoadingMoreMarketplaceProducts) && (
                <div
                  ref={marketplaceLoadMoreRef}
                  className="flex min-h-[90px] items-center justify-center"
                  aria-live="polite"
                >
                  {isLoadingMoreMarketplaceProducts && (
                    <div className="flex items-center gap-2 font-inter text-[13px] text-[#80808C]">
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#D5D7DE] border-t-[#80808C]" />
                      Loading more products...
                    </div>
                  )}
                </div>
              )}
            </>)}
        <FloatingChatBar
          onStartSurvey={handleGuestHeroAction}
          onRequireProfile={openSurveyGate}
          canUseRealChat={canUseRealChat}
          shouldShowSurveyPrompt={shouldShowSurveyPrompt}
          isProfileChecking={isProfileChecking}
          isAuthenticated={isAuthenticated}
        />{" "}
        <AdvancedMeasurementsProModal
          isOpen={advancedMeasurementsModalOpen}
          onClose={() => setAdvancedMeasurementsModalOpen(false)}
        />
      </main>
      <Footer/>
    </div>
    </>
  );
};

export default NewMarketplace;
